import {useCallback} from "react";
import {useFormApi} from "../../../context/ApiContext.tsx";
import {useFormActions, useFormInfo, useFormMethods, useFormStatic} from "../DyanmicFormContext.tsx";
import {BaseFormComponent, DataGridComponent, FrontEndActionJob, FrontEndActionType} from "../DynamicFormTypes.ts";
import {loadingManager} from "../../../context/LoadingManager.ts";
import {extractDataGridKey} from "../dyanimcFormUtil.tsx";


interface UseFieldBlurHandlerProps {
    component: BaseFormComponent
}

export const useFieldBlurHandler = ({component}: UseFieldBlurHandlerProps) => {
    const formApi = useFormApi();
    const { formMethods } = useFormMethods();
    const { formPermissions, flattenedSchemaMap } = useFormStatic();
    const { getActiveFormData } = useFormActions();
    const { applyAutoFillResponse } = useFormInfo();


    const triggerField = (currentFieldKey: string, fieldKeyToTrigger: string) => {
        if (fieldKeyToTrigger === currentFieldKey) {
            return;
        }
        formMethods.trigger(fieldKeyToTrigger)
    };

    const handleFieldBlur = useCallback(async () => {
        console.debug("Field blurred:", component.fieldKey, component);

        if (component.fieldsToTriggerOnBlur && component.fieldsToTriggerOnBlur.length > 0) {
            console.debug("Triggering fields on blur for:", component.fieldKey, component.fieldsToTriggerOnBlur);

            component.fieldsToTriggerOnBlur.forEach((comboKey) => {
                console.debug("Processing trigger for:", comboKey);

                if (!comboKey.dataGrid) {
                    console.debug("Triggering simple field:", comboKey.fieldKey);
                    triggerField(component.fieldKey, comboKey.fieldKey);
                    return;
                }

                if (comboKey.dataGrid) {
                    const dataGridValues = formMethods.getValues(comboKey.dataGrid);
                    if (Array.isArray(dataGridValues)) {
                        //if it's a different grid, trigger all rows
                        dataGridValues.forEach((_, index) => {
                            const targetField = `${comboKey.dataGrid}.${index}.${comboKey.fieldKey}`;
                            console.debug("Triggering data grid field in different grid:", `${comboKey.dataGrid}.${index}.${comboKey.fieldKey}`);
                            triggerField(component.fieldKey, targetField);
                        });
                    }
                }
            });
        }

        if (!component.onFieldBlur) {
            return;
        }

        if (!formPermissions.canEdit) {
            console.info("Auto setting form values is disabled. onFieldBlur action will not set any values.", component.fieldKey);
            return;
        }

        if (component.onFieldBlur.type === FrontEndActionType.API_CALL) {
            if (component.onFieldBlur.job == FrontEndActionJob.COMP_SUBJECT_PROXIMITY) {
                try {
                    const myKey = extractDataGridKey(component.fieldKey);
                    const definition = flattenedSchemaMap.get(myKey.dataGrid!) as DataGridComponent;
                    let fieldKeysToCheck = ["COMP_CITY", "COMP_STATE", "COMP_ZIP", "COMP_ADDRESS"];

                    const isMissingRequiredField = definition.components.some((child) =>
                        child.mapTo &&
                        fieldKeysToCheck.includes(child.mapTo) &&
                        !formMethods.getValues(`${myKey.dataGrid}.${myKey.row}.${child.fieldKey}`)
                    );
                    if (isMissingRequiredField) {
                        return;
                    }
                }
                catch (e) {
                    console.error("failed in fast failing COMP_SUBJECT_PROXIMITY)", e);
                }
            }

            try {
                loadingManager.show();
                const response = await formApi.onBlurCall({
                    blurredComponent: component,
                    activeFormData: getActiveFormData()
                });

                console.log(`API response for ${component.fieldKey} on blur:`, response);
                await applyAutoFillResponse(response, {
                    triggerValidation: true
                });
            } catch (error) {
                console.error(`Error in frontEndActionCall for ${component.fieldKey}:`, error);
            } finally {
                loadingManager.hide();
            }
        }
    }, [component.fieldKey, formPermissions.canEdit]);


    return { handleFieldBlur };
};